import React from 'react';
import { PieChart, Pie, Cell, Tooltip, Legend, ResponsiveContainer } from 'recharts';
import { Card } from 'react-bootstrap';
import { formatCurrency } from '../../shared/utils/currency';

const COLORS = ['#0088FE', '#00C49F', '#FFBB28', '#FF8042', '#8884d8', '#82ca9d', '#ffc658', '#d0ed57', '#a4de6c', '#8dd1e1'];
const DEBT_COLOR = '#e74c3c';

// Latest-month snapshot, one slice per AssetType (debt included, shown by
// magnitude so it renders as a real slice, but its true signed value is
// what's used everywhere else - the tooltip shows that, not the magnitude).
//
// "Latest" isn't simply the max YearMonth string: a single forward-dated
// recurring entry (e.g. a fixed debt pre-entered for a future month before
// that month's other holdings have been recorded) would otherwise look
// like "current" and render as a misleading 100%-of-one-type snapshot.
// Only consider months whose entry count is at least half the fullest
// month on record to count as a real, reasonably-complete snapshot.
const computeLatestAllocation = (data) => {
  if (!data || data.length === 0) return { latestMonth: null, slices: [] };

  const countsByMonth = new Map();
  data.forEach(({ YearMonth }) => countsByMonth.set(YearMonth, (countsByMonth.get(YearMonth) || 0) + 1));
  const maxCount = Math.max(...countsByMonth.values());
  const completeMonths = Array.from(countsByMonth.keys()).filter(m => countsByMonth.get(m) >= maxCount / 2);

  const latestMonth = (completeMonths.length > 0 ? completeMonths : data.map(d => d.YearMonth))
    .reduce((max, m) => (m > max ? m : max));
  const totalsByType = new Map();
  data
    .filter(item => item.YearMonth === latestMonth)
    .forEach(({ AssetType, total_value }) => {
      totalsByType.set(AssetType, (totalsByType.get(AssetType) || 0) + total_value);
    });

  const slices = Array.from(totalsByType.entries())
    .map(([assetType, value]) => ({ assetType, value, magnitude: Math.abs(value), isDebt: value < 0 }))
    .filter(s => s.magnitude > 0)
    .sort((a, b) => b.magnitude - a.magnitude);

  return { latestMonth, slices };
};

const CustomTooltip = ({ active, payload, currency }) => {
  if (active && payload && payload.length) {
    const { assetType, value } = payload[0].payload;
    return (
      <div className="custom-tooltip card p-2">
        <p className="mb-0" style={{ color: payload[0].payload.isDebt ? DEBT_COLOR : payload[0].color, fontWeight: 'bold' }}>
          {`${assetType}: ${formatCurrency(value, currency)}`}
        </p>
      </div>
    );
  }
  return null;
};

const AssetAllocationChart = ({ data, currency }) => {
  const { latestMonth, slices } = computeLatestAllocation(data);

  if (slices.length === 0) {
    return (
      <Card className="mb-4 shadow-lg">
        <Card.Header>Current Allocation</Card.Header>
        <Card.Body>
          <p>No asset data available for charting.</p>
        </Card.Body>
      </Card>
    );
  }

  let colorIndex = 0;

  return (
    <Card className="mb-4 shadow-lg">
      <Card.Header>Current Allocation {latestMonth ? `(as of ${latestMonth})` : ''}</Card.Header>
      <Card.Body style={{ height: '350px' }}>
        <ResponsiveContainer width="100%" height="100%">
          <PieChart>
            <Pie
              data={slices}
              cx="50%"
              cy="50%"
              labelLine={false}
              outerRadius={110}
              innerRadius={55}
              dataKey="magnitude"
              nameKey="assetType"
              label={({ assetType, percent }) => `${assetType} ${(percent * 100).toFixed(0)}%`}
            >
              {slices.map((entry) => (
                <Cell key={entry.assetType} fill={entry.isDebt ? DEBT_COLOR : COLORS[colorIndex++ % COLORS.length]} />
              ))}
            </Pie>
            <Tooltip content={<CustomTooltip currency={currency} />} />
            <Legend />
          </PieChart>
        </ResponsiveContainer>
      </Card.Body>
    </Card>
  );
};

export default AssetAllocationChart;
